import React, { useState, useContext } from "react"
import axios from "axios"
import { Box, TextField } from "@mui/material"
import ReCAPTCHA from "react-google-recaptcha"

import Button from "components/Buttons/Button"
import Feedback from "components/Feedback/Feedback"
import { Email } from "components/i18n/Contact"

import useInputValue from "@secureo/common/hooks/useInputValue"
import AppContext from "@secureo/common/context/AppContext/AppContext"
import { useSnackbar } from "notistack"

const sendContactForm = async (name: string, email: string, message: string) => {
	await axios.post("/api/email/sendContactForm", {
		name,
		email,
		message,
	})
}

const ContactForm = () => {
	const { isLoggedIn, customer } = useContext(AppContext)

	const firstName = customer?.firstName ?? ""
	const lastName = customer?.lastName ?? ""

	const name = useInputValue(customer ? `${firstName} ${lastName}` : "")
	const email = useInputValue(customer?.email ?? "")
	const message = useInputValue("")

	const [isLoading, setIsLoading] = useState(false)
	const [isSent, setIsSent] = useState(false)
	const [isError, setIsError] = useState(false)
	const { enqueueSnackbar } = useSnackbar()

	const [recaptchaValue, setRecaptchaValue] = useState(false)

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		if (recaptchaValue === false) {
			enqueueSnackbar("Bitte bestätigen sie das Captcha.", { variant: "error" })
			return
		}

		setIsSent(false)
		setIsLoading(true)
		setIsError(false)

		try {
			await sendContactForm(name.value, email.value, message.value)
			setIsSent(true)
		} catch (err) {
			console.error(err)
			setIsError(true)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<form onSubmit={onSubmit}>
			<div>
				<TextField
					id="name"
					label="Vor- und Nachname"
					defaultValue={isLoggedIn ? `${firstName} ${lastName}` : ""}
					variant="filled"
					fullWidth
					disabled={isLoading}
					required
					{...name}
				/>
			</div>
			<div>
				<TextField
					id="email"
					label="E-Mail"
					variant="filled"
					type="email"
					fullWidth
					disabled={isLoading}
					required
					{...email}
				/>
			</div>
			<Box mb={2}>
				<TextField
					id="message"
					label="Ihre Nachricht"
					variant="filled"
					multiline
					rows={4}
					fullWidth
					disabled={isLoading}
					required
					{...message}
				/>
			</Box>
			<p style={{ fontSize: 12, color: "#505e5f" }}>
				* Ihre Mailadresse ist nur für die Zusendung einer Kopie der Anfrage erforderlich
				und wird nicht für Marketingzwecke verwendet!
			</p>
			{isSent ? (
				<Feedback type="success">
					Ihre Nachricht wurde erfolgreich gesendet. Sie erhalten in Kürze eine Kopie
					dieser E-Mail.
				</Feedback>
			) : (
				<Box mt={2} style={{ maxWidth: 400, float: "left" }}>
					<Box my={2}>
						<ReCAPTCHA
							size="normal"
							sitekey="6LfAEkMfAAAAAAvoMnu_fAcAUvHzYkLj03lO6193"
							onChange={setRecaptchaValue}
						/>
					</Box>
					<Button color="primary" type="submit" disabled={isLoading}>
						Kontakt aufnehmen
					</Button>
				</Box>
			)}
			{isError && (
				<Box mt={2}>
					<Feedback type="error">
						Etwas ist schiefgelaufen. Bitte kontaktieren Sie uns unter <Email />.
					</Feedback>
				</Box>
			)}
		</form>
	)
}

export default ContactForm
